import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../hooks/useApi';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import DateInput from '../components/DateInput';
import RecetteStockPanel from '../components/RecetteStockPanel';
import { SectionProduitFini } from '../components/TracabiliteSections';
import EtapeTracabilite from '../components/EtapeTracabilite';
import BatchAnalytiquePanel from '../components/BatchAnalytiquePanel';
import CoutRevient from '../components/CoutRevient';
import CourbeCuissonBatch from '../components/CourbeCuissonBatch';
import EnteteBatch from '../components/EnteteBatch';
import { creerFormulaireTraiteFinal } from '../utils/tracabilite';
import Icon from '../components/Icon';
import './ProductionPorc.css';

// ─── CONSTANTES ──────────────────────────────

const ETAPES = [
  { id: 1, label: 'Batch' },
  { id: 2, label: 'Traçabilité' },
  { id: 3, label: 'Cuisson' },
  { id: 4, label: 'Production' },
  { id: 5, label: 'Confirmation' },
];

const FORMATS_DEFAUT = [
  { sku: 'PE400',  label: 'Format 400g', poids_g: 400,  prix: 16.00, quantite: '' },
  { sku: 'PE1000', label: 'Format 1kg',  poids_g: 1000, prix: 30.00, quantite: '' },
];

// ─── STEPPER ─────────────────────────────────

function Stepper({ etape, nbTracabiliteNonLies = 0, onStepClick, batch, isFinalise }) {
  const peutCliquer = (cible) => {
    if (!batch && cible > 1) return false;
    if (cible < etape) return true;
    if (isFinalise) return true;
    if (cible <= etape) return true;
    return etape >= cible - 1;
  };

  return (
    <div className="stepper">
      {ETAPES.map((e, i) => {
        const clickable = onStepClick && peutCliquer(e.id);
        return (
          <React.Fragment key={e.id}>
            <div
              className={`step ${etape === e.id ? 'active' : etape > e.id ? 'done' : ''} ${clickable ? 'step-cliquable' : ''}`}
              style={{ position: 'relative', cursor: clickable ? 'pointer' : 'default' }}
              onClick={() => clickable && onStepClick(e.id)}
              title={clickable ? `Aller à l'étape ${e.id} — ${e.label}` : ''}
            >
              <div className="step-circle">{etape > e.id ? <Icon name="check" size={14} weight="bold" /> : e.id}</div>
              {e.id === 2 && nbTracabiliteNonLies > 0 && (
                <span className="step-badge-warning">{nbTracabiliteNonLies}</span>
              )}
              <span className="step-label">{e.label}</span>
            </div>
            {i < ETAPES.length - 1 && (
              <div className={`step-line ${etape > e.id ? 'done' : ''}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── SECTION FIFO (toujours visible) ─────────

function SectionFIFO() {
  const [stock, setStock]     = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/porc/stock')
      .then(setStock)
      .catch(() => setStock([]))
      .finally(() => setLoading(false));
  }, []);

  const batchesAvecStock = stock.filter(b =>
    b.formats.some(f => f.quantite_dispo > 0)
  );

  return (
    <div className="card fifo-section">
      <div className="fifo-header-row">
        <div className="fifo-title">
          <Icon name="liste" size={15} color="var(--muted)" />
          <span>Traçabilité FIFO — Quel batch utiliser en premier</span>
        </div>
        <p className="muted" style={{ fontSize: 12, marginTop: 6 }}>
          Les batches les plus anciens apparaissent en premier. Toujours épuiser le stock le plus ancien avant d'ouvrir un nouveau batch.
        </p>
      </div>

      {loading ? (
        <div className="skeleton" style={{ height: 60, borderRadius: 'var(--radius-md)' }} />
      ) : batchesAvecStock.length === 0 ? (
        <div className="empty-state" style={{ padding: 'var(--space-5) 0' }}>Aucun stock disponible en ce moment.</div>
      ) : (
        <table className="esc-table">
          <thead>
            <tr>
              <th>Priorité</th>
              <th>Batch</th>
              <th>Date production</th>
              <th>PE400 dispo</th>
              <th>PE1000 dispo</th>
              <th>Yield global</th>
            </tr>
          </thead>
          <tbody>
            {batchesAvecStock.map((b, i) => {
              const pe400  = b.formats.find(f => f.sku === 'PE400');
              const pe1000 = b.formats.find(f => f.sku === 'PE1000');
              return (
                <tr key={b.batch_id}>
                  <td>
                    {i === 0
                      ? <span className="badge badge--success badge-premier">PREMIER</span>
                      : <span className="badge badge-suivant">{i + 1}</span>}
                  </td>
                  <td style={{ fontWeight: 600 }}>{b.batch_nom}</td>
                  <td>{b.date_prod}</td>
                  <td>
                    {pe400
                      ? <span className={pe400.quantite_dispo > 0 ? 'gold' : 'muted'}>{pe400.quantite_dispo} sac{pe400.quantite_dispo !== 1 ? 's' : ''}</span>
                      : <span className="muted">—</span>}
                  </td>
                  <td>
                    {pe1000
                      ? <span className={pe1000.quantite_dispo > 0 ? 'gold' : 'muted'}>{pe1000.quantite_dispo} sac{pe1000.quantite_dispo !== 1 ? 's' : ''}</span>
                      : <span className="muted">—</span>}
                  </td>
                  <td>
                    {b.yield_global > 0
                      ? <span className="gold">{b.yield_global.toFixed(1)}%</span>
                      : <span className="muted">—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── ÉTAPE 1 : BATCH ─────────────────────────

function EtapeBatch({ onBatchCree }) {
  const [batches, setBatches] = useState([]);
  const [mode, setMode]       = useState('liste');
  const [form, setForm]       = useState({
    nom         : '',
    date_prod   : new Date().toISOString().slice(0, 10),
    operateurs  : '',
    fournisseur : '',
    prix_kg     : '',
    poids_cru_kg: '',
    notes_qualite: '',
  });
  const [nomAuto, setNomAuto]       = useState('');
  const [nomManuel, setNomManuel]   = useState(false);
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur]   = useState('');
  const [batchASupprimer, setBatchASupprimer] = useState(null);
  const [suppLoading, setSuppLoading] = useState(false);
  const [toast, setToast] = useState('');
  const [batchAnalytique, setBatchAnalytique] = useState(null);

  useEffect(() => {
    api.get('/porc/batches').then(setBatches).catch(() => {});
  }, []);

  // Auto-numérotation au montage et quand on passe en mode "nouveau"
  useEffect(() => {
    if (mode === 'nouveau' && !nomManuel) {
      api.get('/batches/prochain-numero/porc')
        .then(r => {
          setNomAuto(r.nom);
          setForm(f => ({ ...f, nom: r.nom }));
        })
        .catch(() => {});
    }
  }, [mode]); // eslint-disable-line

  const afficherToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3500);
  };

  const supprimerBatch = async () => {
    if (!batchASupprimer) return;
    setSuppLoading(true);
    try {
      await api.delete(`/porc/batches/${batchASupprimer.id}`);
      setBatches(prev => prev.filter(b => b.id !== batchASupprimer.id));
      afficherToast(`Batch ${batchASupprimer.nom} supprimé ✓`);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur lors de la suppression');
    } finally {
      setSuppLoading(false);
      setBatchASupprimer(null);
    }
  };

  const creerBatch = async () => {
    if (!form.date_prod) {
      setErreur('Date requise');
      return;
    }
    if (!form.operateurs) {
      setErreur('Veuillez sélectionner les opérateurs');
      return;
    }
    setLoading(true);
    setErreur('');
    try {
      const result = await api.post('/porc/batches', {
        ...form,
        // nom omis si vide → backend auto-génère
        nom         : form.nom || undefined,
        prix_kg     : parseFloat(form.prix_kg) || 0,
        poids_cru_kg: parseFloat(form.poids_cru_kg) || 0,
      });
      const listeBatches = await api.get('/porc/batches');
      const nouveau = listeBatches.find(b => b.id === result.id) || { id: result.id, nom: result.nom, ...form };
      onBatchCree(nouveau);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur création batch');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="etape-batch">
      {batchASupprimer && (
        <ConfirmDeleteModal
          nomBatch={batchASupprimer.nom}
          loading={suppLoading}
          onConfirm={supprimerBatch}
          onAnnuler={() => setBatchASupprimer(null)}
        />
      )}
      {batchAnalytique && (
        <div className="modal-overlay" onClick={() => setBatchAnalytique(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 860, maxHeight: '90vh', overflowY: 'auto' }}>
            <BatchAnalytiquePanel batch={batchAnalytique} onFermer={() => setBatchAnalytique(null)} />
          </div>
        </div>
      )}
      {toast && <div className="toast-success">{toast}</div>}
      {erreur && <p className="danger" style={{ marginBottom: 12 }}>{erreur}</p>}

      <div className="etape-actions">
        <button
          className={`btn ${mode === 'liste' ? 'btn-primary' : 'btn-ghost'}`}
          onClick={() => setMode('liste')}
        >
          Batches existants
        </button>
        <button
          className={`btn ${mode === 'nouveau' ? 'btn-primary' : 'btn-ghost'}`}
          onClick={() => setMode('nouveau')}
        >
          + Nouveau batch
        </button>
      </div>

      {mode === 'nouveau' && (
        <div className="card form-card">
          <div className="card-section-header">
            <Icon name="porc" size={15} color="var(--muted)" />
            <span className="form-titre">Nouveau batch porc effiloché</span>
          </div>
          <RecetteStockPanel produit="porc" poidsCruKg={form.poids_cru_kg} />
          <div className="form-grid">
            <div className="form-field">
              <label>Numéro du batch</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  value={form.nom}
                  onChange={e => setForm(f => ({ ...f, nom: e.target.value }))}
                  readOnly={!nomManuel}
                  style={{ flex: 1, opacity: nomManuel ? 1 : 0.75, background: nomManuel ? undefined : 'rgba(255,255,255,0.04)' }}
                  placeholder={nomAuto || 'Chargement…'}
                />
                <button
                  type="button"
                  className="btn btn-ghost btn--sm"
                  onClick={() => setNomManuel(m => !m)}
                  title={nomManuel ? 'Revenir au numéro automatique' : 'Saisir un numéro manuellement'}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {nomManuel
                    ? <><Icon name="retour" size={13} /> Auto</>
                    : <><Icon name="modifier" size={13} /> Modifier</>
                  }
                </button>
              </div>
              <span className="muted" style={{ fontSize: 12 }}>Numéro auto-généré</span>
            </div>
            <div className="form-field">
              <label>Date de production *</label>
              <DateInput
                value={form.date_prod}
                onChange={v => setForm(f => ({ ...f, date_prod: v }))}
              />
            </div>
            <div className="form-field">
              <label>Opérateurs *</label>
              <select
                value={form.operateurs}
                onChange={e => setForm(f => ({ ...f, operateurs: e.target.value }))}
                required
              >
                <option value="">— Sélectionner —</option>
                <option value="LCU">LCU</option>
                <option value="ELA">ELA</option>
                <option value="LCU+ELA">Les deux</option>
              </select>
            </div>
            <div className="form-field">
              <label>Fournisseur</label>
              <input
                value={form.fournisseur}
                onChange={e => setForm(f => ({ ...f, fournisseur: e.target.value }))}
                placeholder="ex: Viandes Gosselin"
              />
            </div>
            <div className="form-field">
              <label>Prix d'achat matière (cru) — $/kg</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.prix_kg}
                onChange={e => setForm(f => ({ ...f, prix_kg: e.target.value }))}
                placeholder="ex: 4.50"
              />
            </div>
            <div className="form-field">
              <label>Poids cru acheté (kg)</label>
              <input
                type="number"
                step="0.1"
                min="0"
                value={form.poids_cru_kg}
                onChange={e => setForm(f => ({ ...f, poids_cru_kg: e.target.value }))}
                placeholder="ex: 15.0"
              />
            </div>
            <div className="form-field">
              <label>Notes qualité</label>
              <input
                value={form.notes_qualite}
                onChange={e => setForm(f => ({ ...f, notes_qualite: e.target.value }))}
                placeholder="Qualité de la viande, observations..."
              />
            </div>
          </div>
          <div style={{ marginTop: 20 }}>
            <button className="btn btn-primary" onClick={creerBatch} disabled={loading || !form.operateurs}>
              {loading ? 'Création...' : 'Créer le batch →'}
            </button>
          </div>
        </div>
      )}

      {mode === 'liste' && (
        <div className="card">
          <h3 className="card-title">Batches porc existants</h3>
          {batches.length === 0 ? (
            <p className="muted">Aucun batch pour l'instant. Créez-en un nouveau.</p>
          ) : (
            <table className="esc-table">
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>Date</th>
                  <th>Fournisseur</th>
                  <th>Prix/kg</th>
                  <th>Poids cru</th>
                  <th>Yield</th>
                  <th>Statut</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {batches.map(b => (
                  <tr key={b.id}>
                    <td style={{ fontWeight: 600 }}>{b.nom}</td>
                    <td>{b.date_prod}</td>
                    <td>{b.fournisseur || <span className="muted">—</span>}</td>
                    <td>{b.prix_kg > 0 ? `${b.prix_kg}$/kg` : <span className="muted">—</span>}</td>
                    <td>{b.poids_cru_kg > 0 ? `${b.poids_cru_kg} kg` : <span className="muted">—</span>}</td>
                    <td>
                      {b.yield_global > 0
                        ? <span className="gold">{b.yield_global.toFixed(1)}%</span>
                        : <span className="muted">—</span>}
                    </td>
                    <td>
                      <span className={`badge ${b.statut === 'finalise' ? 'badge--success' : 'badge--warning'}`}>
                        {b.statut === 'finalise' ? 'Finalisé' : 'En cours'}
                      </span>
                      {b.nb_tracabilite_manquante > 0 && (
                        <span className="badge-tracabilite-warning" style={{ marginLeft: 6 }}>
                          <Icon name="attention" size={11} /> {b.nb_tracabilite_manquante} sans facture
                        </span>
                      )}
                    </td>
                    <td className="td-actions">
                      <button className="btn btn-ghost btn--sm" onClick={() => onBatchCree(b)}>
                        Ouvrir
                      </button>
                      {b.statut === 'finalise' && (
                        <Link className="btn btn-ghost btn--sm" to={`/batches/${b.id}/tracabilite`}>
                          Traçabilité
                        </Link>
                      )}
                      {b.statut === 'finalise' && (
                        <button className="btn btn-ghost btn--sm" onClick={() => setBatchAnalytique(b)} title="Analytique">
                          <Icon name="analytics" size={14} />
                        </button>
                      )}
                      <button
                        className="btn btn-danger btn--sm btn-icon"
                        onClick={() => setBatchASupprimer(b)}
                        title="Supprimer ce batch"
                      >
                        <Icon name="fermer" size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

// ─── PANNEAU TIMER CUISSON (réutilisable) ────
// Gère ▶ Démarrer / ⏹ Arrêter via PATCH /api/batches/:id/cuisson/demarrer|arreter
// heure_debut/heure_fin stockés en ISO 8601 UTC (new Date().toISOString())
// Fallback manuel HH:MM → converti en ISO avec la date du batch

function PanneauTimerCuisson({ batch, onRefresh }) {
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur]         = useState('');
  const [tempsEcoule, setTempsEcoule] = useState('');
  const [heureManuelle, setHeureManuelle] = useState('');

  // Calcule le temps écoulé depuis heure_debut, refresh toutes les 30s
  useEffect(() => {
    if (!batch.heure_debut || batch.heure_fin) return;
    const calculer = () => {
      const debut = new Date(batch.heure_debut);
      const maintenant = new Date();
      const diffMs = maintenant - debut;
      if (diffMs < 0) { setTempsEcoule(''); return; }
      const h = Math.floor(diffMs / 3600000);
      const m = Math.floor((diffMs % 3600000) / 60000);
      setTempsEcoule(`${h}h ${String(m).padStart(2, '0')}min`);
    };
    calculer();
    const interval = setInterval(calculer, 30000);
    return () => clearInterval(interval);
  }, [batch.heure_debut, batch.heure_fin]);

  const fmtHeure = (iso) => {
    if (!iso) return '—';
    // Affiche HH:MM en heure locale
    return new Date(iso).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' });
  };

  const calcDuree = (debut, fin) => {
    if (!debut || !fin) return '';
    const diffMs = new Date(fin) - new Date(debut);
    if (diffMs < 0) return '';
    const h = Math.floor(diffMs / 3600000);
    const m = Math.floor((diffMs % 3600000) / 60000);
    return `${h}h ${String(m).padStart(2, '0')}min`;
  };

  const demarrer = async () => {
    setChargement(true); setErreur('');
    try {
      await api.patch(`/batches/${batch.id}/cuisson/demarrer`);
      onRefresh();
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur démarrage');
    } finally { setChargement(false); }
  };

  const arreter = async () => {
    setChargement(true); setErreur('');
    try {
      await api.patch(`/batches/${batch.id}/cuisson/arreter`);
      onRefresh();
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur arrêt');
    } finally { setChargement(false); }
  };

  const demarrerManuel = async () => {
    if (!heureManuelle) return;
    setChargement(true); setErreur('');
    try {
      // Fallback manuel : prend la date du batch + HH:MM et convertit en ISO UTC
      const isoManuel = new Date(`${batch.date_prod}T${heureManuelle}:00`).toISOString();
      await api.patch(`/batches/${batch.id}/cuisson/demarrer`, { heure_debut: isoManuel });
      onRefresh();
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur démarrage manuel');
    } finally { setChargement(false); }
  };

  return (
    <div className="card timer-cuisson-panel" style={{ marginBottom: 16 }}>
      <div className="timer-cuisson-header">
        <Icon name="timer" size={16} color="var(--muted)" />
        <span className="timer-cuisson-title">Suivi cuisson</span>
      </div>
      {erreur && (
        <div className="error-inline" style={{ marginBottom: 12 }}>
          <Icon name="erreur" size={15} />{erreur}
        </div>
      )}

      {/* État : pas encore démarré */}
      {!batch.heure_debut && (
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <button className="btn btn-primary" onClick={demarrer} disabled={chargement}>
            <Icon name="demarrer" size={15} />
            {chargement ? 'Démarrage...' : 'Démarrer la cuisson'}
          </button>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <div className="field" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: 11 }}>Ou heure de début manuelle</label>
              <input
                type="time"
                value={heureManuelle}
                onChange={e => setHeureManuelle(e.target.value)}
                style={{ width: 130 }}
              />
            </div>
            {heureManuelle && (
              <button className="btn btn-ghost btn--sm" onClick={demarrerManuel} disabled={chargement}>
                Enregistrer
              </button>
            )}
          </div>
        </div>
      )}

      {/* État : démarré, pas encore arrêté */}
      {batch.heure_debut && !batch.heure_fin && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
            <span className="timer-running-badge">
              <Icon name="demarrer" size={13} />
              Démarré à {fmtHeure(batch.heure_debut)}
            </span>
            {tempsEcoule && <span className="timer-elapsed">{tempsEcoule}</span>}
          </div>
          <button className="btn btn-danger" onClick={arreter} disabled={chargement}>
            <Icon name="arreter" size={15} />
            {chargement ? 'Arrêt...' : 'Arrêter la cuisson'}
          </button>
        </div>
      )}

      {/* État : terminé */}
      {batch.heure_debut && batch.heure_fin && (
        <div className="timer-done-info">
          <span className="muted">Démarré {fmtHeure(batch.heure_debut)} → Terminé {fmtHeure(batch.heure_fin)}</span>
          <span className="gold" style={{ fontWeight: 600, fontFamily: 'Cinzel, serif', fontSize: 14 }}>
            Durée : {calcDuree(batch.heure_debut, batch.heure_fin)}
          </span>
        </div>
      )}
    </div>
  );
}

// ─── ÉTAPE 2 : CUISSON ───────────────────────

function EtapeCuisson({ batch, onCuissonSaisie, onBatchRefresh }) {
  const [form, setForm] = useState({
    heures_cuisson: '',
    temp_interne_f: '',
    notes_qualite : batch.notes_qualite || '',
  });
  const [saving, setSaving] = useState(false);
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    if (batch.heures_cuisson > 0 || batch.temp_interne_f > 0) {
      setForm({
        heures_cuisson: String(batch.heures_cuisson || ''),
        temp_interne_f: String(batch.temp_interne_f || ''),
        notes_qualite : batch.notes_qualite || '',
      });
    }
  }, [batch.id]); // eslint-disable-line

  const sauvegarder = async () => {
    setSaving(true);
    setErreur('');
    try {
      await api.put(`/porc/batches/${batch.id}/cuisson`, {
        heures_cuisson: parseFloat(form.heures_cuisson) || 0,
        temp_interne_f: parseFloat(form.temp_interne_f) || 0,
        notes_qualite : form.notes_qualite,
      });
      onCuissonSaisie({ ...form });
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur sauvegarde cuisson');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="etape-cuisson">
      {/* Résumé batch */}
      <div className="card">
        <div className="batch-resume">
          <span className="gold" style={{ fontWeight: 700 }}>{batch.nom}</span>
          <span className="muted">{batch.date_prod}</span>
          {batch.fournisseur && <span className="muted">{batch.fournisseur}</span>}
          {parseFloat(batch.poids_cru_kg) > 0 && <span className="muted">{batch.poids_cru_kg} kg cru</span>}
        </div>
      </div>

      {/* Panneau timer cuisson */}
      <PanneauTimerCuisson batch={batch} onRefresh={onBatchRefresh || (() => {})} />

      {/* Données de cuisson */}
      <div className="card form-card">
        <div className="card-section-header">
          <Icon name="temperature" size={15} color="var(--muted)" />
          <span className="form-titre">Données de cuisson</span>
        </div>
        {erreur && <div className="error-inline" style={{ marginBottom: 12 }}><Icon name="erreur" size={15} />{erreur}</div>}
        <div className="form-grid">
          <div className="form-field">
            <label>Heures de cuisson</label>
            <input
              type="number"
              step="0.5"
              min="0"
              value={form.heures_cuisson}
              onChange={e => setForm(f => ({ ...f, heures_cuisson: e.target.value }))}
              placeholder="ex: 13.5"
            />
          </div>
          <div className="form-field">
            <label>Température interne atteinte (°F)</label>
            <input
              type="number"
              step="1"
              min="0"
              value={form.temp_interne_f}
              onChange={e => setForm(f => ({ ...f, temp_interne_f: e.target.value }))}
              placeholder="ex: 205"
            />
          </div>
          <div className="form-field">
            <label>Notes qualité</label>
            <input
              value={form.notes_qualite}
              onChange={e => setForm(f => ({ ...f, notes_qualite: e.target.value }))}
              placeholder="Observations de cuisson..."
            />
          </div>
        </div>

        <div style={{ marginTop: 20, display: 'flex', gap: 12 }}>
          <button className="btn btn-primary" onClick={sauvegarder} disabled={saving}>
            {saving ? 'Sauvegarde...' : 'Sauvegarder et continuer →'}
          </button>
          <button className="btn btn-ghost" onClick={() => onCuissonSaisie(null)}>
            Passer cette étape
          </button>
        </div>
      </div>

      {/* Suivi de cuisson unifié */}
      <CourbeCuissonBatch batchId={batch.id} produit="porc" sessionActive={true} />
    </div>
  );
}

// ─── ÉTAPE 3 : PRODUCTION ─────────────────────

function EtapeProduction({ batch, yieldStats, onProductionSaisie, formatsInitiaux }) {
  // Init en cascade : (1) formatsInitiaux passés (ex: après finalisation), (2) brouillon DB, (3) défaut
  const [formats, setFormats] = useState(() => {
    if (formatsInitiaux && formatsInitiaux.some(f => parseInt(f.quantite) > 0)) {
      return formatsInitiaux;
    }
    if (batch?.formats_brouillon_json) {
      try {
        const parsed = JSON.parse(batch.formats_brouillon_json);
        if (Array.isArray(parsed) && parsed.length > 0) {
          // Merge avec FORMATS_DEFAUT pour garder labels/prix à jour
          return FORMATS_DEFAUT.map(def => {
            const found = parsed.find(p => p.sku === def.sku);
            return found ? { ...def, quantite: found.quantite } : { ...def };
          });
        }
      } catch { /* fallback */ }
    }
    return FORMATS_DEFAUT.map(f => ({ ...f }));
  });
  const [stockWC, setStockWC]     = useState(null);
  const [loadingWC, setLoadingWC] = useState(true);
  const [erreur, setErreur]       = useState('');
  const [savingBrouillon, setSavingBrouillon] = useState(false);

  useEffect(() => {
    api.get('/porc/stock/wc')
      .then(setStockWC)
      .catch(() => setStockWC(null))
      .finally(() => setLoadingWC(false));
  }, []);

  const setQuantite = (sku, val) => {
    setFormats(fs => fs.map(f => f.sku === sku ? { ...f, quantite: val } : f));
  };

  // Auto-save brouillon (sur blur)
  const sauverBrouillon = async () => {
    if (batch?.statut === 'finalise') return;
    setSavingBrouillon(true);
    try {
      await api.put(`/porc/batches/${batch.id}/formats-brouillon`, {
        formats: formats.map(f => ({ sku: f.sku, quantite: f.quantite })),
      });
    } catch { /* silencieux — ce n'est qu'un brouillon */ }
    finally { setSavingBrouillon(false); }
  };

  const totalSacs   = formats.reduce((s, f) => s + (parseInt(f.quantite) || 0), 0);
  const totalPoids  = formats.reduce((s, f) => s + ((parseInt(f.quantite) || 0) * f.poids_g / 1000), 0);
  const totalValeur = formats.reduce((s, f) => s + ((parseInt(f.quantite) || 0) * f.prix), 0);
  const prixKg      = parseFloat(batch.prix_kg) || 0;
  const poidsCru    = parseFloat(batch.poids_cru_kg) || 0;
  const cout        = totalPoids * prixKg;

  // Yield global estimé (calculé live)
  const yieldGlobalEst = (poidsCru > 0 && totalPoids > 0)
    ? Math.round(totalPoids / poidsCru * 1000) / 10
    : null;
  const yieldMoyenGlobal = yieldStats?.yield_moyen_global;
  let badgeYieldGlobal = null;
  if (yieldGlobalEst !== null && yieldMoyenGlobal !== null) {
    const diff = Math.round((yieldGlobalEst - yieldMoyenGlobal) * 10) / 10;
    badgeYieldGlobal = {
      classe: diff >= 0 ? 'yield-badge-bon' : 'yield-badge-bas',
      texte : diff >= 0 ? `+${diff}% vs moyenne` : `${diff}% vs moyenne`,
    };
  }

  const continuer = () => {
    const formatsValides = formats.filter(f => parseInt(f.quantite) > 0);
    if (formatsValides.length === 0) {
      setErreur('Saisissez au moins une quantité');
      return;
    }
    setErreur('');
    onProductionSaisie(formats);
  };

  return (
    <div className="etape-production">
      <div className="card">
        <div className="batch-resume">
          <span className="gold" style={{ fontWeight: 700 }}>{batch.nom}</span>
          <span className="muted">{batch.date_prod}</span>
          {batch.fournisseur && <span className="muted">{batch.fournisseur}</span>}
          {prixKg > 0 && <span className="muted">{prixKg} $/kg</span>}
          {batch.yield_cuisson > 0 && (
            <span className="muted">Yield cuisson : <span className="gold">{batch.yield_cuisson.toFixed(1)}%</span></span>
          )}
        </div>
      </div>

      {/* Stock WooCommerce actuel */}
      <div className="card stock-wc-card">
        <div className="card-section-header">
          <Icon name="boutique" size={15} color="var(--muted)" />
          <span className="form-titre">Stock actuel sur WooCommerce</span>
        </div>
        {loadingWC ? (
          <p className="muted">Chargement du stock en ligne...</p>
        ) : !stockWC || !stockWC.connected ? (
          <p className="muted">WooCommerce non connecté — le stock sera mis à jour manuellement.</p>
        ) : (
          <div className="stock-wc-grid">
            {['PE400', 'PE1000'].map(sku => {
              const s = stockWC[sku];
              const label = sku === 'PE400' ? 'Format 400g' : 'Format 1kg';
              const qte = parseInt(formats.find(f => f.sku === sku)?.quantite) || 0;
              const apres = (s?.stock || 0) + qte;
              return (
                <div className="stock-wc-item" key={sku}>
                  <span className="stock-wc-sku">{sku}</span>
                  <span className="stock-wc-label">{label}</span>
                  <span className="stock-wc-qty gold">{s ? s.stock : '—'} sacs</span>
                  {qte > 0 && (
                    <span className="success" style={{ fontSize: 12 }}>→ {apres} après push</span>
                  )}
                  <span className="muted" style={{ fontSize: 12 }}>en ligne actuellement</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Saisie des quantités */}
      <div className="card">
        <div className="card-section-header">
          <Icon name="package" size={15} color="var(--muted)" />
          <span className="form-titre">Quantités produites</span>
        </div>
        {erreur && <div className="error-inline" style={{ marginBottom: 12 }}><Icon name="erreur" size={15} />{erreur}</div>}

        <table className="esc-table formats-table">
          <thead>
            <tr>
              <th>Format</th>
              <th>SKU</th>
              <th>Quantité produite</th>
              <th>Prix unitaire</th>
              <th>Stock WC après push</th>
              <th>Sous-total</th>
            </tr>
          </thead>
          <tbody>
            {formats.map(f => {
              const qte = parseInt(f.quantite) || 0;
              const stockActuel = stockWC?.[f.sku]?.stock || 0;
              return (
                <tr key={f.sku}>
                  <td style={{ fontWeight: 600 }}>{f.label}</td>
                  <td><span className="muted">{f.sku}</span></td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      className="quantite-input"
                      value={f.quantite}
                      onChange={e => setQuantite(f.sku, e.target.value)}
                      onBlur={sauverBrouillon}
                      placeholder="0"
                    />
                  </td>
                  <td className="gold">{f.prix.toFixed(2)} $</td>
                  <td>
                    {qte > 0 && stockWC?.connected ? (
                      <span>
                        <span className="muted">{stockActuel}</span>
                        <span className="muted"> → </span>
                        <span className="success">{stockActuel + qte}</span>
                      </span>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td className={qte > 0 ? 'gold' : 'muted'}>
                    {qte > 0 ? `${(qte * f.prix).toFixed(2)} $` : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Résumé yield live */}
        {totalSacs > 0 && poidsCru > 0 && (
          <div className="yield-live-panel">
            <div className="yield-live-item">
              <span className="yield-live-label">Poids vendable</span>
              <span className="yield-live-valeur gold">{totalPoids.toFixed(2)} kg</span>
            </div>
            <div className="yield-live-sep">→</div>
            <div className="yield-live-item">
              <span className="yield-live-label">Yield estimé</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="yield-live-valeur gold">
                  {yieldGlobalEst !== null ? `${yieldGlobalEst.toFixed(1)}%` : '—'}
                </span>
                {badgeYieldGlobal && (
                  <span className={`yield-badge ${badgeYieldGlobal.classe}`}>{badgeYieldGlobal.texte}</span>
                )}
              </div>
            </div>
            <div className="yield-live-sep">→</div>
            <div className="yield-live-item">
              <span className="yield-live-label">Pertes estimées</span>
              <span className="yield-live-valeur warning">
                {(poidsCru - totalPoids).toFixed(2)} kg
              </span>
            </div>
          </div>
        )}

        {/* Résumé financier */}
        {totalSacs > 0 && (
          <div className="resume-production">
            <div className="resume-item">
              <span className="resume-label">Total sacs</span>
              <span className="resume-value gold">{totalSacs}</span>
            </div>
            {poidsCru === 0 && (
              <div className="resume-item">
                <span className="resume-label">Poids vendable</span>
                <span className="resume-value">{totalPoids.toFixed(2)} kg</span>
              </div>
            )}
            {prixKg > 0 && (
              <div className="resume-item">
                <span className="resume-label">Coût matière</span>
                <span className="resume-value danger">{cout.toFixed(2)} $</span>
              </div>
            )}
            <div className="resume-item">
              <span className="resume-label">Valeur de vente</span>
              <span className="resume-value success">{totalValeur.toFixed(2)} $</span>
            </div>
            {prixKg > 0 && (
              <div className="resume-item">
                <span className="resume-label">Marge estimée</span>
                <span className={`resume-value ${totalValeur - cout >= 0 ? 'success' : 'danger'}`}>
                  {((totalValeur - cout) / totalValeur * 100).toFixed(1)}%
                </span>
              </div>
            )}
          </div>
        )}

        <div style={{ marginTop: 20 }}>
          <button className="btn btn-primary" onClick={continuer} disabled={totalSacs === 0}>
            Continuer →
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── ÉTAPE 4 : CONFIRMATION & PUSH ───────────

function EtapeConfirmation({ batch, formats, onRetour }) {
  const [loading, setLoading]           = useState(false);
  const [resultat, setResultat]         = useState(null);
  const [erreur, setErreur]             = useState('');
  const [stockWC, setStockWC]           = useState(null);
  const [apercuDeduction, setApercuDeduction] = useState(null);
  const [finalForm, setFinalForm] = useState(() => {
    const defaut = creerFormulaireTraiteFinal('porc');
    return {
      ...defaut,
      date_congelation: batch.date_congelation || defaut.date_congelation,
      date_expiration_produit: batch.date_expiration_produit || defaut.date_expiration_produit,
      temp_conservation: batch.temp_conservation || defaut.temp_conservation,
      incident: batch.incident || '',
    };
  });

  const formatsValidesInit = formats.filter(f => parseInt(f.quantite) > 0);
  const totalSacsInit      = formatsValidesInit.reduce((s, f) => s + parseInt(f.quantite), 0);

  useEffect(() => {
    api.get('/porc/stock/wc').then(setStockWC).catch(() => {});
  }, []);

  useEffect(() => {
    if (totalSacsInit > 0 && batch.statut !== 'finalise') {
      api.get(`/porc/batches/${batch.id}/apercu-deduction?nb_sacs=${totalSacsInit}`)
        .then(setApercuDeduction)
        .catch(() => {});
    }
  }, [totalSacsInit, batch.id, batch.statut]); // eslint-disable-line

  const formatsValides  = formats.filter(f => parseInt(f.quantite) > 0);
  const totalSacs       = formatsValides.reduce((s, f) => s + parseInt(f.quantite), 0);
  const totalPoids      = formatsValides.reduce((s, f) => s + (parseInt(f.quantite) * f.poids_g / 1000), 0);
  const totalValeur     = formatsValides.reduce((s, f) => s + (parseInt(f.quantite) * f.prix), 0);
  const prixKg          = parseFloat(batch.prix_kg) || 0;
  const poidsCru        = parseFloat(batch.poids_cru_kg) || 0;
  const cout            = totalPoids * prixKg;
  const yieldGlobalEst  = (poidsCru > 0 && totalPoids > 0)
    ? Math.round(totalPoids / poidsCru * 1000) / 10
    : null;

  const confirmer = async () => {
    setLoading(true);
    setErreur('');
    try {
      const res = await api.post(`/porc/batches/${batch.id}/production`, {
        formats: formatsValides.map(f => ({ sku: f.sku, quantite: parseInt(f.quantite) })),
        ...finalForm,
      });
      setResultat(res);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur lors de la confirmation');
    } finally {
      setLoading(false);
    }
  };

  const dejaFinalise = batch.statut === 'finalise' && !resultat;

  if (dejaFinalise) {
    return (
      <div className="etape-confirmation">
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <h3 className="card-title" style={{ margin: 0 }}>Récapitulatif — Batch {batch.nom}</h3>
            <span className="badge badge--success"><Icon name="check" size={12} weight="bold" /> Finalisé le {batch.date_prod}</span>
          </div>

          <table className="esc-table">
            <thead>
              <tr>
                <th>Format</th>
                <th>SKU</th>
                <th>Quantité produite</th>
                <th>Prix unitaire</th>
                <th>Valeur</th>
              </tr>
            </thead>
            <tbody>
              {formatsValides.map(f => {
                const qte = parseInt(f.quantite);
                return (
                  <tr key={f.sku}>
                    <td style={{ fontWeight: 600 }}>{f.label}</td>
                    <td className="muted">{f.sku}</td>
                    <td className="gold">{qte} sacs</td>
                    <td>{f.prix.toFixed(2)} $</td>
                    <td>{(qte * f.prix).toFixed(2)} $</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="resume-production">
            <div className="resume-item">
              <span className="resume-label">Total sacs</span>
              <span className="resume-value gold">{totalSacs}</span>
            </div>
            <div className="resume-item">
              <span className="resume-label">Poids vendable</span>
              <span className="resume-value">{totalPoids.toFixed(2)} kg</span>
            </div>
            {yieldGlobalEst !== null && (
              <div className="resume-item">
                <span className="resume-label">Yield global</span>
                <span className="resume-value gold">{yieldGlobalEst.toFixed(1)}%</span>
              </div>
            )}
            {prixKg > 0 && (
              <div className="resume-item">
                <span className="resume-label">Coût matière</span>
                <span className="resume-value danger">{cout.toFixed(2)} $</span>
              </div>
            )}
            <div className="resume-item">
              <span className="resume-label">Valeur de vente</span>
              <span className="resume-value success">{totalValeur.toFixed(2)} $</span>
            </div>
          </div>

          <div style={{ marginTop: 20, display: 'flex', gap: 12, alignItems: 'center' }}>
            <button className="btn btn-ghost" onClick={() => onRetour()}>
              <Icon name="retour" size={14} /> Retour à la liste
            </button>
            <button className="btn btn-primary" disabled style={{ opacity: 0.5, cursor: 'not-allowed' }}>
              <Icon name="verrou" size={14} /> Batch déjà finalisé
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (resultat) {
    return (
      <div className="etape-confirmation">
        <div className="card success-card">
          <div className="success-icon">
            <Icon name="succes" size={32} weight="fill" />
          </div>
          <h2 className="success-titre">Production enregistrée !</h2>
          <p className="muted">Batch <strong className="gold">{batch.nom}</strong> finalisé avec succès.</p>

          <div className="success-details">
            {resultat.formats_produits.map(f => (
              <div key={f.sku} className="success-format">
                <span className="success-format-nom">{f.nom}</span>
                <span className="gold">{f.quantite} sacs</span>
              </div>
            ))}
          </div>

          {resultat.yield_global > 0 && (
            <div className="yield-display" style={{ marginTop: 16 }}>
              <div className="yield-principal">
                <span className="yield-label">Yield global final</span>
                <span className="yield-valeur gold">{resultat.yield_global.toFixed(1)}%</span>
              </div>
            </div>
          )}

          {resultat.wc_sync && (
            <div className="wc-sync-result">
              {resultat.wc_sync.success > 0 && (
                <p className="success" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Icon name="check" size={14} weight="bold" />
                  {resultat.wc_sync.success} produit{resultat.wc_sync.success > 1 ? 's' : ''} mis à jour sur WooCommerce
                </p>
              )}
              {resultat.wc_sync.errors?.map((e, i) => (
                <p key={i} className="danger" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Icon name="attention" size={14} /> {e}
                </p>
              ))}
            </div>
          )}

          {resultat.ecriture_id && (
            <p className="muted" style={{ marginTop: 12, fontSize: 13 }}>
              Écriture comptable #{resultat.ecriture_id} créée automatiquement.
            </p>
          )}

          {resultat.deductions && resultat.deductions.length > 0 && (
            <div style={{ marginTop: 16, textAlign: 'left' }}>
              <h4 style={{ marginBottom: 10 }}>Inventaire déduit</h4>
              <table className="esc-table">
                <thead>
                  <tr><th>Ingrédient</th><th>Déduit</th><th>Stock restant</th></tr>
                </thead>
                <tbody>
                  {resultat.deductions.map(d => (
                    <tr key={d.matiere_id}>
                      <td>{d.nom}</td>
                      <td className="gold">−{d.quantite_deduite} {d.unite}</td>
                      <td>
                        <span className={d.statut === 'negatif' ? 'danger' : d.statut === 'bas' ? 'warning' : 'success'}>
                          {d.stock_apres} {d.unite}
                          {d.statut === 'bas' && <Icon name="attention" size={12} />}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {resultat.deductions.some(d => d.statut !== 'ok') && (
                <p className="warning" style={{ marginTop: 8, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Icon name="attention" size={14} /> {resultat.deductions.filter(d => d.statut !== 'ok').length} ingrédient(s) à réapprovisionner
                </p>
              )}
            </div>
          )}

          <CoutRevient analytique={resultat.analytique} />

          <CourbeCuissonBatch batchId={batch.id} produit="porc" sessionActive={false} />

          <div style={{ marginTop: 24, display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button className="btn btn-primary" onClick={onRetour}>
              Nouveau batch
            </button>
            <Link className="btn btn-ghost" to={`/batches/${batch.id}/tracabilite`}>
              Fiche de traçabilité
            </Link>
            <a
              href="https://escamel.ca/wp-admin/edit.php?post_type=product"
              target="_blank"
              rel="noreferrer"
              className="btn btn-ghost"
            >
              Voir sur WooCommerce →
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="etape-confirmation">
      <div className="card">
        <h3>Récapitulatif — Batch {batch.nom}</h3>
        {erreur && <p className="danger" style={{ marginBottom: 12 }}>{erreur}</p>}

        <table className="esc-table">
          <thead>
            <tr>
              <th>Format</th>
              <th>SKU</th>
              <th>Quantité</th>
              <th>Prix unitaire</th>
              <th>Valeur</th>
              {stockWC?.connected && <th>Stock WC après push</th>}
            </tr>
          </thead>
          <tbody>
            {formatsValides.map(f => {
              const qte         = parseInt(f.quantite);
              const stockActuel = stockWC?.[f.sku]?.stock || 0;
              return (
                <tr key={f.sku}>
                  <td style={{ fontWeight: 600 }}>{f.label}</td>
                  <td className="muted">{f.sku}</td>
                  <td className="gold">{qte} sacs</td>
                  <td>{f.prix.toFixed(2)} $</td>
                  <td>{(qte * f.prix).toFixed(2)} $</td>
                  {stockWC?.connected && (
                    <td>
                      <span className="muted">{stockActuel}</span>
                      <span className="muted"> → </span>
                      <span className="success">{stockActuel + qte}</span>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="resume-production">
          <div className="resume-item">
            <span className="resume-label">Total sacs</span>
            <span className="resume-value gold">{totalSacs}</span>
          </div>
          <div className="resume-item">
            <span className="resume-label">Poids vendable</span>
            <span className="resume-value">{totalPoids.toFixed(2)} kg</span>
          </div>
          {yieldGlobalEst !== null && (
            <div className="resume-item">
              <span className="resume-label">Yield global</span>
              <span className="resume-value gold">{yieldGlobalEst.toFixed(1)}%</span>
            </div>
          )}
          {prixKg > 0 && (
            <div className="resume-item">
              <span className="resume-label">Coût matière estimé</span>
              <span className="resume-value danger">{cout.toFixed(2)} $</span>
            </div>
          )}
          <div className="resume-item">
            <span className="resume-label">Valeur de vente</span>
            <span className="resume-value success">{totalValeur.toFixed(2)} $</span>
          </div>
        </div>

        <div className="confirmation-note">
          <p>
            Le stock WooCommerce sera <strong>additionné</strong> au stock existant
            (pas remplacé). Une écriture comptable sera créée automatiquement.
          </p>
        </div>

        {/* Stats live — Rentabilité (avant finalisation, calcul live côté frontend) */}
        {apercuDeduction && totalSacs > 0 && (() => {
          const coutTotal     = apercuDeduction.cout_total_batch != null ? apercuDeduction.cout_total_batch : null;
          const coutParSac    = (coutTotal != null && totalSacs > 0)    ? Math.round(coutTotal / totalSacs * 100) / 100 : null;
          const coutParKg     = (coutTotal != null && totalPoids > 0)   ? Math.round(coutTotal / totalPoids * 100) / 100 : null;
          const margeBrute    = (totalValeur > 0 && coutTotal != null)
            ? Math.round((totalValeur - coutTotal) / totalValeur * 10000) / 100
            : null;
          return (
            <div className="rentabilite-grid-card">
              <h4 className="rentabilite-titre">Rentabilité — calcul live</h4>
              <div className="rentabilite-grid">
                <div className="rentabilite-item">
                  <div className="rentabilite-label">Coût matières / sac</div>
                  <div className="rentabilite-valeur gold">{coutParSac != null ? `${coutParSac.toFixed(2)} $` : '—'}</div>
                </div>
                <div className="rentabilite-item">
                  <div className="rentabilite-label">Coût matières / kg</div>
                  <div className="rentabilite-valeur gold">{coutParKg != null ? `${coutParKg.toFixed(2)} $/kg` : '—'}</div>
                </div>
                <div className="rentabilite-item">
                  <div className="rentabilite-label">
                    Yield (cuit / cru)
                    {yieldGlobalEst == null && <Icon name="info" size={12} color="var(--muted)" style={{ marginLeft: 4 }} title="Saisis le poids cru à l'étape 1" />}
                  </div>
                  <div className={`rentabilite-valeur ${yieldGlobalEst == null ? 'muted' : yieldGlobalEst >= 60 ? 'success' : yieldGlobalEst >= 45 ? 'warning' : 'danger'}`}>
                    {yieldGlobalEst != null ? `${yieldGlobalEst.toFixed(1)} %` : 'N/A'}
                  </div>
                  {poidsCru > 0 && (
                    <div className="muted" style={{ fontSize: 11 }}>{totalPoids.toFixed(2)} kg / {poidsCru} kg</div>
                  )}
                </div>
                <div className="rentabilite-item">
                  <div className="rentabilite-label">Revenu estimé</div>
                  <div className="rentabilite-valeur gold">{totalValeur > 0 ? `${totalValeur.toFixed(2)} $` : '—'}</div>
                </div>
                <div className="rentabilite-item">
                  <div className="rentabilite-label">Marge brute</div>
                  <div className={`rentabilite-valeur ${margeBrute == null ? 'muted' : margeBrute >= 50 ? 'success' : margeBrute >= 30 ? 'warning' : 'danger'}`}>
                    {margeBrute != null ? `${margeBrute.toFixed(1)} %` : '—'}
                  </div>
                </div>
                <div className="rentabilite-item">
                  <div className="rentabilite-label">Sacs produits</div>
                  <div className="rentabilite-valeur gold">{totalSacs}</div>
                </div>
              </div>
              {yieldGlobalEst == null && (
                <p className="muted" style={{ fontSize: 12, marginTop: 10, marginBottom: 0 }}>
                  Saisis le <strong>poids cru acheté (kg)</strong> à l'étape 1 pour activer le yield.
                </p>
              )}
            </div>
          );
        })()}

        {/* Aperçu déduction inventaire */}
        {apercuDeduction && apercuDeduction.deductions && apercuDeduction.deductions.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <h4 style={{ marginBottom: 10 }}>Aperçu — Déduction inventaire</h4>
            <table className="esc-table">
              <thead>
                <tr><th>Ingrédient</th><th>Déduit</th><th>Stock après</th></tr>
              </thead>
              <tbody>
                {apercuDeduction.deductions.map(d => (
                  <tr key={d.matiere_id}>
                    <td>{d.nom}</td>
                    <td className="gold">−{d.quantite_deduite} {d.unite}</td>
                    <td>
                      <span className={d.statut === 'negatif' ? 'danger' : d.statut === 'bas' ? 'warning' : 'success'}>
                        {d.stock_apres} {d.unite}
                        {d.statut === 'negatif' && <Icon name="erreur" size={12} />}
                        {d.statut === 'bas' && <Icon name="attention" size={12} />}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {apercuDeduction.deductions.some(d => d.statut !== 'ok') && (
              <p className="warning" style={{ marginTop: 8, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Icon name="attention" size={14} /> {apercuDeduction.deductions.filter(d => d.statut !== 'ok').length} ingrédient(s) passeront sous le seuil après déduction.
              </p>
            )}
          </div>
        )}

        <SectionProduitFini
          produit="porc"
          form={finalForm}
          setForm={setFinalForm}
          batchId={batch.id}
          batchStatut={batch.statut}
        />

        <div style={{ marginTop: 20, display: 'flex', gap: 12, alignItems: 'center' }}>
          <button className="btn btn-ghost" onClick={() => onRetour()}>
            ← Retour
          </button>
          <button className="btn btn-primary" onClick={confirmer} disabled={loading}>
            {loading ? 'Enregistrement...' : 'Confirmer et mettre à jour WooCommerce'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── ÉTAPE 1 BIS — Édition batch porc ouvert ─

function EtapeEditerPorc({ batch, isFinalise, onUpdated, onChangerBatch }) {
  const [prixKg,     setPrixKg]     = useState(String(batch?.prix_kg      || ''));
  const [poidsCruKg, setPoidsCruKg] = useState(String(batch?.poids_cru_kg || ''));

  const sauvegarderSpecifiques = useCallback(async (champNom, valeur) => {
    if (!batch?.id) return;
    try {
      const payload = { [champNom]: parseFloat(valeur) || null };
      const updated = await api.put(`/porc/batches/${batch.id}`, payload);
      if (onUpdated) onUpdated(updated);
    } catch {
      // Erreur silencieuse — EnteteBatch gère les siennes
    }
  }, [batch?.id, onUpdated]); // eslint-disable-line

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <button className="btn btn-ghost btn-sm" onClick={onChangerBatch}>
          ← Changer de batch
        </button>
      </div>
      <EnteteBatch
        apiBase="/porc"
        batch={batch}
        onUpdated={onUpdated}
      >
        {/* Champs spécifiques porc — auto-save sur blur */}
        <div className="form-grid">
          <div className="form-group">
            <label>Prix d'achat matière (cru) — $/kg</label>
            <input
              className="form-input"
              type="number"
              step="0.01"
              min="0"
              value={prixKg}
              onChange={e => setPrixKg(e.target.value)}
              onBlur={() => sauvegarderSpecifiques('prix_kg', prixKg)}
              placeholder="ex : 4.50"
            />
          </div>
          <div className="form-group">
            <label>Poids cru acheté (kg)</label>
            <input
              className="form-input"
              type="number"
              step="0.1"
              min="0"
              value={poidsCruKg}
              onChange={e => setPoidsCruKg(e.target.value)}
              onBlur={() => sauvegarderSpecifiques('poids_cru_kg', poidsCruKg)}
              disabled={isFinalise}
              placeholder="ex : 15.0"
            />
          </div>
        </div>
      </EnteteBatch>
    </div>
  );
}

// ─── COMPOSANT PRINCIPAL ─────────────────────

export default function ProductionPorc() {
  const [etape, setEtape]                       = useState(1);
  const [batch, setBatch]                       = useState(null);
  const [formats, setFormats]                   = useState([]);
  const [yieldStats, setYieldStats]             = useState(null);
  const [nbTracabiliteNonLies, setNbTracabiliteNonLies] = useState(0);
  const isFinalise = batch?.statut === 'finalise';

  useEffect(() => {
    api.get('/porc/yield/stats').then(setYieldStats).catch(() => {});
  }, []);

  const onBatchCree = (b) => {
    setBatch(b);
    if (b.statut === 'finalise') {
      const formatsPreRemplis = FORMATS_DEFAUT.map(f => ({
        ...f,
        quantite: f.sku === 'PE400'  ? String(b.total_pe400  || 0) :
                  f.sku === 'PE1000' ? String(b.total_pe1000 || 0) : '0',
      }));
      setFormats(formatsPreRemplis);
      setEtape(5);
    } else {
      setEtape(2);
    }
  };

  const onCuissonSaisie = (data) => {
    if (data && batch) {
      setBatch(prev => ({
        ...prev,
        heures_cuisson: parseFloat(data.heures_cuisson) || prev.heures_cuisson,
        temp_interne_f: parseFloat(data.temp_interne_f) || prev.temp_interne_f,
      }));
    }
    setEtape(4);
  };

  // Recharge le batch depuis l'API (utilisé par PanneauTimerCuisson pour rafraîchir heure_debut/heure_fin)
  // Utilise /batches/:id/tracabilite qui retourne garantiment heure_debut/heure_fin depuis la table batches
  const refreshBatch = useCallback(() => {
    if (!batch?.id) return;
    api.get(`/batches/${batch.id}/tracabilite`)
      .then(d => setBatch(prev => ({ ...prev, heure_debut: d.heure_debut, heure_fin: d.heure_fin })))
      .catch(() => {});
  }, [batch?.id]); // eslint-disable-line

  const onProductionSaisie = (formatsRemplis) => {
    setFormats(formatsRemplis);
    setEtape(5);
  };

  const retourDebut = () => {
    setBatch(null);
    setFormats([]);
    setEtape(1);
  };

  const handleStepClick = (cible) => {
    // L'étape 1 avec un batch ouvert affiche la liste + le batch courant.
    // Ne pas appeler retourDebut() ici — le bouton « ← Changer de batch » le fait.
    if (!batch && cible > 1) return;
    if (cible < etape) { setEtape(cible); return; }
    if (isFinalise) { setEtape(cible); return; }
    if (cible <= etape + 1) setEtape(cible);
  };

  return (
    <div className="production-porc">
      <div className="page-header">
        <div>
          <h1 className="page-title">Production — Porc Effiloché</h1>
          <p className="page-subtitle">Yield tracking · WooCommerce · FIFO</p>
        </div>
        <Link to="/porc/recommandation" className="btn btn-ghost btn--sm">
          <Icon name="analytics" size={14} /> Calculateur d'achat
        </Link>
      </div>

      <Stepper
        etape={etape}
        nbTracabiliteNonLies={nbTracabiliteNonLies}
        onStepClick={handleStepClick}
        batch={batch}
        isFinalise={isFinalise}
      />

      <div className="etape-contenu">
        {etape === 1 && !batch && <EtapeBatch onBatchCree={onBatchCree} />}
        {etape === 1 && batch && (
          <EtapeEditerPorc
            batch={batch}
            isFinalise={isFinalise}
            onUpdated={updated => setBatch(prev => ({ ...prev, ...updated }))}
            onChangerBatch={retourDebut}
          />
        )}
        {etape === 2 && batch && (
          <EtapeTracabilite
            batchId={batch?.id}
            batchNom={batch?.nom}
            batchStatut={batch?.statut}
            onNext={() => setEtape(3)}
            onBack={() => setEtape(1)}
            onNbNonLiesChange={setNbTracabiliteNonLies}
          />
        )}
        {etape === 3 && batch && (
          <EtapeCuisson
            batch={batch}
            onCuissonSaisie={onCuissonSaisie}
            onBatchRefresh={refreshBatch}
          />
        )}
        {etape === 4 && batch && (
          <EtapeProduction
            batch={batch}
            yieldStats={yieldStats}
            onProductionSaisie={onProductionSaisie}
            formatsInitiaux={formats.length > 0 ? formats : null}
          />
        )}
        {etape === 5 && batch && (
          <EtapeConfirmation
            batch={batch}
            formats={formats.length > 0 ? formats : FORMATS_DEFAUT}
            onRetour={retourDebut}
          />
        )}
      </div>

      <SectionFIFO />
    </div>
  );
}
